import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShopFilters from '@/components/feature/ShopFilters';
import { products, Product } from '@/mocks/products';

const BADGE_KEYS: Record<string, string> = {
  'Mais Vendido': 'badge_best_seller',
  'Novo': 'badge_new',
  'Premium': 'badge_premium',
  'Cask Strength': 'badge_cask_strength',
  'Clássico': 'badge_classic',
  'Exclusivo': 'badge_exclusive',
  'Edição Limitada': 'badge_limited',
  'Navy Strength': 'badge_navy',
  'Importado': 'badge_imported',
};

const DESC_EN: Record<string, string> = {
  'ardmore-islay-single-malt': 'Intense peat, sea smoke and wild honey notes. An Islay classic.',
  'glen-speyside-12': 'Ripe fruits, flower honey and gentle spices. The elegance of Speyside.',
  'highland-reserve-18': 'Rare complexity. Sherry, dark fruits and oriental spices in perfect harmony.',
  'blended-malt-reserve': 'The art of blending. Smoothness, depth and balance in every sip.',
  'peat-monster-cask': 'Extreme peat, cask strength. For uncompromising smoke lovers.',
  'lowland-gentle-spirit': 'Soft, floral and delicate. The perfect entry into the world of single malts.',
  'botanica-london-dry': 'Classic juniper, Sicilian lemon peel and coriander. The perfect London gin.',
  'floral-botanical-gin': 'Rose, lavender, hibiscus and violet petals. A floral gin of rare delicacy.',
  'small-batch-spiced-gin': 'Pink pepper, cardamom, ginger and juniper. Bold, spiced and memorable.',
  'mediterranean-dry-gin': 'Seville orange, thyme, rosemary and juniper. The Mediterranean in a bottle.',
  'navy-strength-gin': 'Power and character. Intense juniper, spices and naval strength in every sip.',
  'japanese-yuzu-gin': 'Yuzu, green tea, sakura and juniper. Japanese delicacy in gin form.',
};

const ShopProductCard = ({ product }: { product: Product }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const displayBadge = product.badge ? (BADGE_KEYS[product.badge] ? t(BADGE_KEYS[product.badge], product.badge) : product.badge) : null;
  const displayDesc = isEn ? (DESC_EN[product.slug] ?? product.description) : product.description;

  return (
    <div className="group relative bg-[#111111] border border-white/8 rounded-xl overflow-hidden hover:border-[#c9a84c]/40 transition-all duration-500" data-product-shop>
      {displayBadge && (
        <div className="absolute top-4 left-4 z-10 bg-[#c9a84c] text-black text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-full whitespace-nowrap">
          {displayBadge}
        </div>
      )}
      <div className="absolute top-4 right-4 z-10 bg-black/60 backdrop-blur-sm text-white/60 text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-white/10 whitespace-nowrap">
        {product.category === 'whisky' ? t('card_cat_whisky', 'Whisky') : t('card_cat_gin', 'Gin')}
      </div>
      <Link to={`/produto/${product.slug}`} className="block">
        <div className="relative h-64 bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] flex items-center justify-center overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="h-56 w-auto object-contain object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-60" />
        </div>
      </Link>
      <div className="p-5">
        <p className="text-[#c9a84c]/70 text-[10px] tracking-[0.25em] uppercase mb-1 font-medium">
          {product.origin}{product.age ? ` · ${product.age}` : ''} · {product.abv}
        </p>
        <Link to={`/produto/${product.slug}`}>
          <h3 className="text-white text-lg font-light mb-1 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {product.name}
          </h3>
        </Link>
        <p className="text-white/40 text-xs mb-4 leading-relaxed line-clamp-2">{displayDesc}</p>
        <div className="flex items-center justify-between">
          <p className="text-[#c9a84c] text-xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {product.priceFormatted}
          </p>
          <Link
            to={`/produto/${product.slug}`}
            className="bg-[#c9a84c] hover:bg-[#b8963e] text-black text-[10px] tracking-widest uppercase font-bold px-5 py-2.5 rounded-md transition-all duration-300 whitespace-nowrap"
          >
            {t('shop_buy', 'Comprar')}
          </Link>
        </div>
      </div>
    </div>
  );
};

const ShopPage = () => {
  const { t } = useTranslation();
  const [sort, setSort] = useState('featured');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');

  const SORT_OPTIONS = [
    { value: 'featured', label: t('shop_featured', 'Destaques') },
    { value: 'price-asc', label: t('shop_price_asc', 'Menor Preço') },
    { value: 'price-desc', label: t('shop_price_desc', 'Maior Preço') },
    { value: 'name-asc', label: t('shop_name_az', 'A–Z') },
  ];

  const CATEGORY_OPTIONS = [
    { value: 'all', label: t('shop_all', 'Todos') },
    { value: 'whisky', label: t('shop_whiskies', 'Whiskies') },
    { value: 'gin', label: t('shop_gins', 'Gins') },
  ];

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== 'all') list = list.filter(p => p.category === category);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.origin.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q)
      );
    }
    if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
    else if (sort === 'name-asc') list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return list;
  }, [sort, category, search]);

  return (
    <>
      <Navbar />
      <main className="bg-[#0a0a0a] min-h-screen pt-[72px]">
        {/* Hero banner */}
        <div className="relative h-52 md:h-64 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=premium%20spirits%20collection%20whisky%20gin%20bottles%20dark%20elegant%20bar%20shelf%20luxury%20distillery%20atmospheric%20moody%20photography%20golden%20amber%20light%20cinematic&width=1600&height=400&seq=shop-hero&orientation=landscape')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
          <div className="relative z-10 h-full flex flex-col items-start justify-center px-8 md:px-16">
            <p className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase mb-3 font-medium">
              {t('shop_hero_label', 'Ma Gádeni')}
            </p>
            <h1 className="text-4xl md:text-5xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t('shop_title', 'Nossa Loja')}
            </h1>
            <div className="flex items-center gap-2 mt-3 text-xs text-white/40 tracking-widest uppercase">
              <Link to="/" className="hover:text-[#c9a84c] transition-colors">{t('nav_home', 'Home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/60">{t('nav_shop', 'Loja')}</span>
            </div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 xl:px-16 py-14">
          <ShopFilters
            sortOptions={SORT_OPTIONS}
            sortValue={sort}
            onSortChange={setSort}
            filterGroups={[
              {
                label: t('shop_category', 'Categoria'),
                key: 'category',
                options: CATEGORY_OPTIONS,
                value: category,
                onChange: setCategory,
              },
            ]}
            totalCount={filtered.length}
            searchValue={search}
            onSearchChange={setSearch}
          />

          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <i className="ri-search-line text-4xl text-white/20 mb-4 block"></i>
              <p className="text-white/40 text-sm">{t('shop_empty')}</p>
              <button
                onClick={() => { setSearch(''); setCategory('all'); }}
                className="mt-4 text-[#c9a84c] text-xs tracking-widest uppercase cursor-pointer hover:underline"
              >
                {t('shop_clear')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map(p => <ShopProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ShopPage;
